import { useCallback, useEffect, useRef, useState } from 'react';

type AsyncAction = () => void | Promise<void>;

const LOADING_MIN_DELAY = 1000;
const ERROR_DISMISS_DELAY = 5000;

function useAsyncAction(action?: AsyncAction, errorText = 'Ha ocurrido un error. Por favor, intenta nuevamente.') {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isMounted = useRef(true);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);

  const scheduleWhileMounted = useCallback((callback: () => void, delay: number) => {
    timers.current.push(setTimeout(() => {
      if (isMounted.current) {
        callback();
      }
    }, delay));
  }, []);

  const run = useCallback(() => {
    if (!action || isLoading) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    Promise.resolve()
      .then(() => action())
      .catch((error) => {
        console.error(errorText, error);

        if (isMounted.current) {
          const publicDetail = typeof error?.publicDetail === 'string' ? error.publicDetail : '';
          setErrorMessage(publicDetail ? `${errorText} ${publicDetail}` : errorText);
        }
      })
      .finally(() => scheduleWhileMounted(() => setIsLoading(false), LOADING_MIN_DELAY));
  }, [action, errorText, isLoading, scheduleWhileMounted]);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    const timer = setTimeout(() => {
      if (isMounted.current) {
        setErrorMessage(null);
      }
    }, ERROR_DISMISS_DELAY);

    return () => clearTimeout(timer);
  }, [errorMessage]);

  return { isLoading, errorMessage, run };
}

export default useAsyncAction;
